import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Lock, ShieldCheck, Globe2, Inbox, Check, MapPin,
  Phone, Mail, QrCode, Printer, Play, Edit3,
} from "lucide-react";
import { useI18n } from "../i18n";
import api from "../api";
import ShareMenu from "../components/ShareMenu";
import { LLC, isLLCFormed } from "../legal";

function videoEmbedSrc(url) {
  if (!url) return null;
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  // Vimeo
  const v = url.match(/vimeo\.com\/(\d+)/);
  if (v) return { kind: "iframe", src: `https://player.vimeo.com/video/${v[1]}` };
  // Direct mp4 / webm
  if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) return { kind: "video", src: url };
  return null;
}

export default function SalesKit() {
  const { lang, setLang } = useI18n();
  const [stats, setStats] = useState(null);
  const [videoUrl, setVideoUrl] = useState(() => localStorage.getItem("cd_sk_video") || "");
  const [editingVideo, setEditingVideo] = useState(false);
  const [tempVideo, setTempVideo] = useState("");

  useEffect(() => { api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  function saveVideo() {
    setVideoUrl(tempVideo);
    localStorage.setItem("cd_sk_video", tempVideo);
    setEditingVideo(false);
  }

  const registerUrl = `${window.location.origin}/register`;
  const salesKitUrl = `${window.location.origin}/sales-kit`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=0&data=${encodeURIComponent(registerUrl)}&color=09090B&bgcolor=F4F4F0`;
  const videoEmbed = videoEmbedSrc(videoUrl);

  const C = {
    en: {
      kicker: "SALES KIT · CORRIDOR DIGITAL · 2026",
      pageTitle: "The corridor's IMMEX directory of record.",
      pageSub: "Bilingual profiles. Compliance vault. Live US-buyer dashboards. Built 45 minutes from your plant.",
      problemTitle: "The problem",
      problemBody: "$2.3B in US-Mexico trade flows through Juárez–El Paso every day. 73% of IMMEX plants in Juárez have no English digital presence. US procurement managers can't find them. Revenue routes elsewhere.",
      solutionTitle: "What you get",
      features: [
        ["Corporate Microsite", "A bilingual ES/EN page that looks like a real spec sheet — capabilities, certifications, plant size, contact. Shareable URL."],
        ["Compliance Vault", "Upload IMMEX, USMCA, ISO documents. Generate secure time-limited share links for US buyers. No login required for them."],
        ["Cross-Border Data Bridge", "Your US partner sees your compliance score in real time. Green / yellow / red. Live document expirations. The only platform in the corridor with this."],
      ],
      caseTitle: "Case study · Industrias Frontera MX",
      caseStats: [
        ["$2.4M", "Initial contract"],
        ["6 wks", "Bilingual platform live"],
        ["12", "Page-1 keywords"],
        ["4", "US RFQs in 30 days"],
      ],
      caseQuote: "They told us nobody targets maquiladoras. We told them — exactly.",
      caseAttrib: "— Derick Varela, Founder · Apex Digital · Las Cruces, NM",
      pricingTitle: "Pricing",
      pricing: [
        ["Listed", "$0", "Free public profile + directory listing"],
        ["Pro", "$199 / mo", "Microsite + vault + RFQ inbox + verification"],
        ["Premium", "$599 / mo", "+ Cross-Border Data Bridge + featured placement"],
        ["IMMEX 4.0", "$85K+", "Custom build · ERP integration · onsite onboarding"],
      ],
      foundingTitle: "Founding 10 — by invitation",
      foundingBody: "We're selecting ten Juárez manufacturers to lock $149/mo for life, get a Corridor Verified badge from day one, and shape what we build next. Application on the back page.",
      ctaTitle: "Get listed in 90 seconds",
      ctaBody: "Scan the QR or visit corridordigital.net",
      ctaBullets: [
        "No credit card to start",
        "Bilingual onboarding included on free tier",
        "60-day money-back guarantee on paid tiers",
      ],
      contactTitle: "Talk to the founder",
      contactName: "Derick Varela",
      contactRole: "Founder · Corridor Digital · Apex Digital",
      contactCity: "Las Cruces, NM · 45 min from Juárez",
      printBtn: "Print this kit",
      backBtn: "Back to site",
      docTitle: "CORRIDOR DIGITAL · SALES KIT · 2026.01",
    },
    es: {
      kicker: "KIT DE VENTAS · CORRIDOR DIGITAL · 2026",
      pageTitle: "El directorio IMMEX oficial del corredor.",
      pageSub: "Perfiles bilingües. Bóveda de cumplimiento. Dashboards en vivo para compradores en EE.UU. Hecho a 45 minutos de tu planta.",
      problemTitle: "El problema",
      problemBody: "$2.3B en comercio EE.UU.–México pasan por Juárez–El Paso cada día. 73% de las plantas IMMEX en Juárez no tienen presencia digital en inglés. Los compradores en EE.UU. no las encuentran. Ese dinero se va a otro lado.",
      solutionTitle: "Lo que obtienes",
      features: [
        ["Micrositio Corporativo", "Una página ES/EN bilingüe que se ve como una hoja técnica real — capacidades, certificaciones, tamaño de planta, contacto. URL para compartir."],
        ["Bóveda de Cumplimiento", "Sube IMMEX, USMCA, ISO. Genera enlaces seguros con caducidad para compradores EE.UU. Ellos no necesitan cuenta."],
        ["Cross-Border Data Bridge", "Tu partner en EE.UU. ve tu puntaje de cumplimiento en tiempo real. Verde / amarillo / rojo. Vencimientos en vivo. La única plataforma en el corredor con esto."],
      ],
      caseTitle: "Caso · Industrias Frontera MX",
      caseStats: [
        ["$2.4M", "Contrato inicial"],
        ["6 sem", "Plataforma bilingüe en vivo"],
        ["12", "Keywords en página 1"],
        ["4", "Cotizaciones EE.UU. en 30 días"],
      ],
      caseQuote: "Nos dijeron que nadie le apunta a las maquiladoras. Nosotros dijimos — exacto.",
      caseAttrib: "— Derick Varela, Fundador · Apex Digital · Las Cruces, NM",
      pricingTitle: "Precios",
      pricing: [
        ["Listado", "$0", "Perfil público y listado gratis"],
        ["Pro", "$199 / mes", "Micrositio + bóveda + bandeja RFQ + verificación"],
        ["Premium", "$599 / mes", "+ Cross-Border Data Bridge + posición destacada"],
        ["IMMEX 4.0", "$85K+", "Construcción a medida · integración ERP · visita en planta"],
      ],
      foundingTitle: "Founding 10 — por invitación",
      foundingBody: "Estamos seleccionando diez fabricantes de Juárez para fijar $149/mes de por vida, obtener insignia Corredor Verificado desde día uno, y decidir lo que construimos después. Aplicación al reverso.",
      ctaTitle: "Lístate en 90 segundos",
      ctaBody: "Escanea el QR o visita corridordigital.net",
      ctaBullets: [
        "Sin tarjeta de crédito para empezar",
        "Onboarding bilingüe incluido en plan gratis",
        "Garantía de 60 días en planes pagados",
      ],
      contactTitle: "Habla con el fundador",
      contactName: "Derick Varela",
      contactRole: "Fundador · Corridor Digital · Apex Digital",
      contactCity: "Las Cruces, NM · a 45 min de Juárez",
      printBtn: "Imprimir kit",
      backBtn: "Volver al sitio",
      docTitle: "CORRIDOR DIGITAL · KIT DE VENTAS · 2026.01",
    },
  };
  const c = C[lang];

  return (
    <div data-testid="sales-kit-page" className="sales-kit">
      {/* Toolbar — hidden on print */}
      <div className="no-print" style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div className="container-cd py-4 flex items-center justify-between gap-4">
          <div className="overline">{c.kicker}</div>
          <div className="flex items-center gap-3">
            <div className="flex border border-black">
              <button onClick={() => setLang("es")} className="px-2.5 py-1 font-mono text-xs" style={{ background: lang === "es" ? "var(--ink)" : "transparent", color: lang === "es" ? "white" : "var(--ink)" }}>ES</button>
              <button onClick={() => setLang("en")} className="px-2.5 py-1 font-mono text-xs" style={{ background: lang === "en" ? "var(--ink)" : "transparent", color: lang === "en" ? "white" : "var(--ink)" }}>EN</button>
            </div>
            <Link to="/" className="btn btn-ghost btn-sm">{c.backBtn}</Link>
            <ShareMenu
              url={salesKitUrl}
              title="Corridor Digital — IMMEX directory of record"
              text="Bilingual platform for IMMEX manufacturers in the Chihuahua–El Paso corridor (Juárez, Parral, Chihuahua City)."
              testid="saleskit-share"
            />
            <button onClick={() => window.print()} className="btn btn-primary btn-sm" data-testid="sales-kit-print">
              <Printer size={12} /> {c.printBtn}
            </button>
          </div>
        </div>
      </div>

      {/* PAGE 1 — front of one-pager */}
      <article className="sales-kit-page">
        <div className="container-cd py-12 md:py-16">
          {/* Header band */}
          <div className="flex items-center justify-between mb-10 pb-6" style={{ borderBottom: "2px solid var(--ink)" }}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center font-display text-white" style={{ background: "var(--ink)" }}>CD</div>
              <div className="leading-tight">
                <div className="font-display text-lg">CORRIDOR DIGITAL</div>
                <div className="overline" style={{ fontSize: 9 }}>CHIHUAHUA ↔ EL PASO</div>
              </div>
            </div>
            <div className="text-right font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              {c.docTitle}
            </div>
          </div>

          <h1 className="font-display leading-[0.92]" style={{ fontSize: "clamp(36px, 5vw, 64px)" }}>
            {c.pageTitle}
          </h1>
          <p className="mt-4 text-lg md:text-xl" style={{ color: "var(--ink-2)", maxWidth: 760 }}>
            {c.pageSub}
          </p>

          {/* FOUNDER VIDEO — sits between title and stats */}
          <section className="mt-10" data-testid="founder-video-section">
            {videoEmbed ? (
              <div className="relative cd-card overflow-hidden" style={{ aspectRatio: "16 / 9", padding: 0 }}>
                {videoEmbed.kind === "iframe" ? (
                  <iframe
                    src={videoEmbed.src}
                    title="Founder video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ width: "100%", height: "100%", border: 0 }}
                  />
                ) : (
                  <video controls src={videoEmbed.src} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                )}
                <button
                  onClick={() => { setTempVideo(videoUrl); setEditingVideo(true); }}
                  className="no-print absolute top-3 right-3 p-1 px-2 font-mono text-xs"
                  style={{ background: "var(--ink)", color: "white" }}
                  data-testid="edit-video-btn"
                >
                  <Edit3 size={11} className="inline mr-1" /> EDIT
                </button>
              </div>
            ) : (
              <div
                className="cd-card no-print p-6 flex items-center gap-4"
                style={{ background: "var(--surface)", borderStyle: "dashed" }}
                data-testid="add-video-empty"
              >
                <Play size={22} />
                <div className="flex-1">
                  <div className="overline">FOUNDER VIDEO · 30-SECOND PITCH</div>
                  <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                    Walk-in prospects convert 3-5× higher with a real human on the page. Paste a YouTube, Vimeo or .mp4 URL.
                  </div>
                </div>
                <button onClick={() => { setTempVideo(""); setEditingVideo(true); }} className="btn btn-dark btn-sm" data-testid="add-video-btn">
                  Add video
                </button>
              </div>
            )}
            {editingVideo && (
              <div className="no-print mt-3 cd-card p-4" data-testid="video-editor">
                <label className="overline">PASTE VIDEO URL</label>
                <input
                  className="cd-input-box mt-2"
                  value={tempVideo}
                  onChange={(e) => setTempVideo(e.target.value)}
                  placeholder="https://youtu.be/… or https://vimeo.com/… or https://yourcdn.com/video.mp4"
                  data-testid="video-url-input"
                />
                <div className="flex gap-2 mt-3 justify-end">
                  <button onClick={() => setEditingVideo(false)} className="btn btn-ghost btn-sm">Cancel</button>
                  {videoUrl && (
                    <button
                      onClick={() => { setVideoUrl(""); localStorage.removeItem("cd_sk_video"); setEditingVideo(false); }}
                      className="btn btn-ghost btn-sm"
                      data-testid="video-remove"
                    >
                      Remove video
                    </button>
                  )}
                  <button onClick={saveVideo} className="btn btn-primary btn-sm" data-testid="video-save">Save</button>
                </div>
                <div className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  ⓘ Saved locally on this device. Stays on your phone. Edit anytime.
                </div>
              </div>
            )}
          </section>

          {/* Problem */}
          <section className="mt-12 grid md:grid-cols-12 gap-8">
            <div className="md:col-span-3">
              <div className="overline">{c.problemTitle}</div>
            </div>
            <div className="md:col-span-9">
              <p className="text-base md:text-lg leading-relaxed">{c.problemBody}</p>
              <div className="mt-6 grid grid-cols-4 gap-0" style={{ border: "1px solid var(--line)" }}>
                {[
                  ["73%", "no EN presence"],
                  ["330+", "IMMEX plants"],
                  ["$2.3B", "daily trade"],
                  ["0", "competitors"],
                ].map(([v, l], i) => (
                  <div key={i} className="p-4" style={{ background: "white", borderRight: i < 3 ? "1px solid var(--line)" : "none" }}>
                    <div className="font-display text-2xl md:text-3xl" style={{ color: "var(--orange)" }}>{v}</div>
                    <div className="overline mt-1" style={{ fontSize: 9 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Solution */}
          <section className="mt-12 grid md:grid-cols-12 gap-8">
            <div className="md:col-span-3">
              <div className="overline">{c.solutionTitle}</div>
            </div>
            <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-3 gap-0" style={{ border: "1px solid var(--line)" }}>
              {c.features.map(([t, d], i) => {
                const Icons = [Globe2, ShieldCheck, Inbox];
                const Icon = Icons[i];
                return (
                  <div key={i} className="p-5" style={{ background: "white", borderRight: i < 2 ? "1px solid var(--line)" : "none" }}>
                    <Icon size={20} strokeWidth={1.5} />
                    <div className="font-heading font-semibold text-base mt-4">{t}</div>
                    <p className="mt-2 text-xs leading-relaxed" style={{ color: "var(--ink-2)" }}>{d}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Case Study */}
          <section className="mt-12 cd-card p-6" style={{ borderColor: "var(--orange)", background: "var(--surface)" }}>
            <div className="overline" style={{ color: "var(--orange)" }}>{c.caseTitle}</div>
            <blockquote className="font-display text-2xl md:text-3xl mt-3 leading-tight">
              "{c.caseQuote}"
            </blockquote>
            <div className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>{c.caseAttrib}</div>
            <div className="mt-5 grid grid-cols-4 gap-0" style={{ border: "1px solid var(--line)" }}>
              {c.caseStats.map(([v, l], i) => (
                <div key={i} className="p-3" style={{ background: "white", borderRight: i < 3 ? "1px solid var(--line)" : "none" }}>
                  <div className="font-display text-xl md:text-2xl" style={{ color: "var(--orange)" }}>{v}</div>
                  <div className="overline mt-1" style={{ fontSize: 9 }}>{l}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </article>

      {/* PAGE BREAK */}
      <div className="page-break" />

      {/* PAGE 2 — pricing + CTA + founder */}
      <article className="sales-kit-page">
        <div className="container-cd py-12 md:py-16">
          {/* Pricing */}
          <section>
            <div className="overline">{c.pricingTitle}</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-0" style={{ border: "1px solid var(--line)" }}>
              {c.pricing.map(([name, price, blurb], i) => {
                const isPro = i === 1;
                const isPremium = i === 2;
                const isEnt = i === 3;
                return (
                  <div
                    key={i}
                    className="p-5 relative"
                    style={{
                      background: isPro ? "var(--orange)" : isEnt ? "var(--ink)" : "white",
                      color: (isPro || isEnt) ? "white" : "var(--ink)",
                      borderRight: i < 3 ? "1px solid var(--line)" : "none",
                    }}
                  >
                    {isPro && (
                      <div className="absolute top-0 right-0 px-2 py-0.5 font-mono" style={{ background: "var(--ink)", color: "white", fontSize: 9 }}>
                        ★ POPULAR
                      </div>
                    )}
                    <div className="overline" style={{ color: isPro || isEnt ? "rgba(255,255,255,0.7)" : "var(--ink-2)" }}>
                      {name}
                    </div>
                    <div className="font-display text-2xl md:text-3xl mt-2">{price}</div>
                    <div className="mt-3 text-xs leading-relaxed" style={{ color: isPro || isEnt ? "rgba(255,255,255,0.85)" : "var(--ink-2)" }}>
                      {blurb}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Founding 10 + CTA */}
          <section className="mt-12 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="overline" style={{ color: "var(--orange)" }}>
                <Lock size={11} className="inline mr-2 -mt-1" /> {c.foundingTitle}
              </div>
              <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--ink)" }}>
                {c.foundingBody}
              </p>
              <div className="mt-6 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                {stats?.founding_spots_remaining ?? 10} / 10 {lang === "es" ? "lugares fundadores disponibles" : "founding spots remaining"}
              </div>
            </div>
            <div className="md:col-span-5 cd-card p-6 text-center" style={{ background: "var(--bg)" }}>
              <div className="overline">{c.ctaTitle}</div>
              <img src={qrSrc} alt="Scan to register" className="mx-auto mt-4" style={{ width: 180, height: 180 }} />
              <div className="mt-4 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                {c.ctaBody}
              </div>
              <div className="mt-4 font-display text-base">corridordigital.net</div>
            </div>
          </section>

          {/* CTA bullets + guarantee */}
          <section className="mt-10 grid md:grid-cols-3 gap-0" style={{ border: "1px solid var(--line)" }}>
            {c.ctaBullets.map((b, i) => (
              <div key={i} className="p-4 flex items-start gap-3" style={{ background: "white", borderRight: i < 2 ? "1px solid var(--line)" : "none" }}>
                <Check size={14} style={{ color: "var(--orange)", marginTop: 4, flexShrink: 0 }} />
                <span className="text-sm">{b}</span>
              </div>
            ))}
          </section>

          {/* Founder card */}
          <section className="mt-12 cd-card p-6" style={{ background: "var(--ink)", color: "white" }}>
            <div className="grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-3 text-center">
                <div className="w-24 h-24 mx-auto flex items-center justify-center font-display text-3xl" style={{ background: "var(--orange)", color: "white" }}>
                  DV
                </div>
              </div>
              <div className="md:col-span-9">
                <div className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>{c.contactTitle}</div>
                <div className="font-display text-2xl mt-2">{c.contactName}</div>
                <div className="font-mono text-xs mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>{c.contactRole}</div>
                <div className="font-mono text-xs mt-1" style={{ color: "rgba(255,255,255,0.5)" }} data-testid="saleskit-llc-tagline">
                  {isLLCFormed ? LLC.name : LLC.pending_label}
                  {" · "}
                  {LLC.registered_address_line_2}
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 font-mono text-xs">
                  <div className="flex items-center gap-2"><MapPin size={12} /> {c.contactCity}</div>
                  <div className="flex items-center gap-2"><Mail size={12} /> {LLC.founder_email}</div>
                  <div className="flex items-center gap-2"><Phone size={12} /> WhatsApp on request</div>
                </div>
              </div>
            </div>
          </section>

          <div className="mt-10 text-center font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            © {new Date().getFullYear()} CORRIDOR DIGITAL · {isLLCFormed ? LLC.name.toUpperCase() : LLC.pending_label.toUpperCase()} · LAS CRUCES, NM · v0.1
          </div>
        </div>
      </article>

      {/* Print styles */}
      <style>{`
        .sales-kit-page { background: white; }
        @media print {
          .no-print { display: none !important; }
          .sales-kit-page { page-break-after: always; }
          .page-break { page-break-before: always; }
          header, footer, .marquee, [data-testid="site-footer"], [data-testid="main-nav"] { display: none !important; }
          body { background: white !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </div>
  );
}
